const RESPONSE_DATA_KEYS = ["data", "result", "payload"];
const FILTER_KEYS = ["filters", "feature_filters", "featureFilters"];
const SKU_KEYS = ["skus", "matching_skus", "matchingSkus", "items", "rows", "data"];
const COUNT_KEYS = ["sku_count", "skuCount", "matching_sku_count", "matchingSkuCount", "count", "total", "total_count"];

// Feature/combination values come back from the source data in inconsistent
// casing (ALL CAPS, all lowercase, mixed) — normalize display to Title Case
// (first letter of each word capitalized) everywhere they're shown, without
// touching raw identifiers like SKU codes/descriptions.
export const toTitleCase = (value) => {
  if (value === null || value === undefined) return value;
  const str = String(value).trim();
  if (!str) return str;

  return str
    .toLowerCase()
    .replace(/(^|[\s/&()-])([a-z])/g, (_, sep, char) => sep + char.toUpperCase());
};

export const unwrapResponse = (response) => {
  let current = response;

  for (const key of RESPONSE_DATA_KEYS) {
    if (current && typeof current === "object" && current[key] !== undefined && current[key] !== null) {
      current = current[key];
    }
  }

  return current;
};

export const normalizeProductTypes = (response) => {
  const payload = unwrapResponse(response);
  const productTypes = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.product_types)
    ? payload.product_types
    : Array.isArray(payload?.productTypes)
      ? payload.productTypes
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

  return productTypes
    .map((item) => {
      const value =
        typeof item === "string"
          ? item
          : item?.product_type ||
            item?.productType ||
            item?.PRODUCT_TYPE ||
            item?.Product_Type ||
            item?.["Product Type"] ||
            item?.name ||
            item?.label ||
            item?.value;
      return value ? { label: String(value), value: String(value) } : null;
    })
    .filter(Boolean);
};

export const normalizeFeatureFilters = (response) => {
  const payload = unwrapResponse(response);
  const filters = FILTER_KEYS.reduce((found, key) => found || payload?.[key], null) || payload || {};
  const skuCount = getFirstNumber(payload, COUNT_KEYS);

  if (!filters || Array.isArray(filters) || typeof filters !== "object") {
    return { filters: {}, skuCount };
  }

  const normalizedFilters = Object.entries(filters).reduce((acc, [filterName, values]) => {
    if (COUNT_KEYS.includes(filterName) || FILTER_KEYS.includes(filterName)) return acc;

    acc[filterName] = normalizeFilterValues(values);
    return acc;
  }, {});

  return { filters: normalizedFilters, skuCount };
};

export const normalizeSkuResponse = (response) => {
  const payload = unwrapResponse(response);
  const skus = SKU_KEYS.reduce(
    (found, key) => found || (Array.isArray(payload?.[key]) ? payload[key] : null),
    null
  ) || (Array.isArray(payload) ? payload : []);

  const total = getFirstNumber(payload, COUNT_KEYS) ?? skus.length;

  return {
    skus: skus.map(normalizeSku).filter(Boolean),
    total,
  };
};

export const normalizeFeatureCombinations = (response) => {
  const payload = unwrapResponse(response);
  const combinations = Array.isArray(payload?.combinations) ? payload.combinations : [];
  const total = getFirstNumber(payload, ["total_combinations", ...COUNT_KEYS]) ?? combinations.length;

  return {
    combinations: combinations.map(normalizeCombination).filter(Boolean),
    total,
    limit: Number(payload?.limit) || combinations.length,
    offset: Number(payload?.offset) || 0,
  };
};

const normalizeCombination = (item) => {
  if (!item || typeof item !== "object") return null;

  const combination =
    item.combination && typeof item.combination === "object" ? item.combination : {};
  const skus = Array.isArray(item.skus) ? item.skus.map(normalizeCombinationSku).filter(Boolean) : [];

  return {
    combinationId: item.combination_id || item.combinationId || JSON.stringify(combination),
    combination,
    skuCount: Number(item.sku_count ?? item.skuCount) || skus.length,
    skus,
  };
};

const normalizeCombinationSku = (sku) => {
  if (!sku || typeof sku !== "object") return null;
  const skuCode = sku.sku_code || sku.skuCode || sku.code;
  if (!skuCode) return null;
  const description = sku.sku_description || sku.skuDescription || sku.description;

  return {
    ...sku,
    _rowId: String(skuCode),
    _skuCode: String(skuCode),
    _description: description ? String(description) : "-",
  };
};

export const normalizeSelectedFiltersForApi = (selectedFilters) =>
  Object.entries(selectedFilters || {}).reduce((acc, [key, value]) => {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    if (!values.length) return acc;
    acc[key] = values.length === 1 ? values[0] : values;
    return acc;
  }, {});

export const getFeatureSkuCode = (sku) =>
  sku?.sku_code ||
  sku?.skuCode ||
  sku?.code ||
  sku?.id ||
  sku?.SKU_CODE ||
  sku?._skuCode;

export const getFeatureSkuId = (sku) =>
  sku?.sku_id ||
  sku?.skuId ||
  sku?.id ||
  sku?.code ||
  sku?.sku_code ||
  sku?.skuCode ||
  sku?._rowId;

export const getFeatureSkuName = (sku) =>
  sku?.sku_name ||
  sku?.skuName ||
  sku?.sku_description ||
  sku?.skuDescription ||
  sku?.description ||
  sku?.name ||
  sku?._description;

export const normalizeFeatureFiltersForUi = (filters) =>
  Object.entries(filters || {}).reduce((acc, [key, value]) => {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    if (values.length) acc[key] = values.map(String);
    return acc;
  }, {});

// One-to-one tiered matching of feature-master SKUs to hierarchy records.
// Exact matches run globally FIRST so every record is claimed by its rightful
// owner before any fuzzy rule can steal it for the wrong SKU. Each hierarchy
// record can be claimed by at most one feature SKU.
const matchFeatureSkusToHierarchy = (featureSkus, hierarchySkus) => {
  const matches = new Map(); // sku._rowId → hierarchy record
  const claimed = new Set(); // hierarchy records already taken

  const norm = (v) => (v === null || v === undefined ? "" : String(v).trim().toLowerCase());
  const recordName = (r) =>
    norm(r.name ?? r.sku_name ?? r.skuName ?? String(r.breadcrumb || r.combination || "").split("\\").pop());
  const recordCode = (r) => {
    const explicit = norm(r.sku_code ?? r.skuCode ?? r.code ?? r.SKU_CODE);
    if (explicit) return explicit;
    // Hierarchy names are usually "CODE-Description"; extract the code prefix
    const name = recordName(r);
    const dash = name.indexOf("-");
    return dash > 0 ? name.slice(0, dash).trim() : "";
  };
  const recordSkuId = (r) => norm(r.sku_id ?? r.skuId ?? String(r.id || "").split("_").pop());

  // Ordered strictest → loosest. A later pass only sees SKUs/records
  // that every earlier pass left unmatched/unclaimed.
  const passes = [
    (sku, r) => { const id = norm(getFeatureSkuId(sku)); return !!id && recordSkuId(r) === id; },
    (sku, r) => { const code = norm(getFeatureSkuCode(sku)); return !!code && recordCode(r) === code; },
    (sku, r) => { const name = norm(getFeatureSkuName(sku)); return !!name && recordName(r) === name; },
    (sku, r) => { const code = norm(getFeatureSkuCode(sku)); return !!code && recordName(r).startsWith(code); },
    (sku, r) => { const code = norm(getFeatureSkuCode(sku)); return !!code && recordName(r).includes(code); },
    (sku, r) => {
      const name = norm(getFeatureSkuName(sku));
      const rn = recordName(r);
      return !!name && !!rn && (rn.includes(name) || name.includes(rn));
    },
  ];

  passes.forEach((test) => {
    featureSkus.forEach((sku) => {
      if (matches.has(sku._rowId)) return;
      const record = hierarchySkus.find((r) => !claimed.has(r) && test(sku, r));
      if (record) {
        matches.set(sku._rowId, record);
        claimed.add(record);
      }
    });
  });

  return matches;
};

export const normalizeFeatureSkuSelectionForStage3 = (
  selectedSkus,
  productType,
  hierarchySkus = []
) => {
  const seenOriginalIds = new Set();
  const uniqueSkus = (selectedSkus || []).filter((sku) => {
    const originalId = sku._rowId;
    if (!originalId || seenOriginalIds.has(originalId)) return false;
    seenOriginalIds.add(originalId);
    return true;
  });

  const hasHierarchy = Array.isArray(hierarchySkus) && hierarchySkus.length > 0;
  const matchMap = hasHierarchy
    ? matchFeatureSkusToHierarchy(uniqueSkus, hierarchySkus)
    : new Map();

  const seenProductIds = new Set();
  return uniqueSkus
    .filter((sku) => !hasHierarchy || matchMap.has(sku._rowId))
    .map((sku) =>
      normalizeFeatureSkuForStage3(sku, productType, hierarchySkus, matchMap.get(sku._rowId) ?? null)
    )
    .filter((product) => {
      if (!product) return false;
      if (seenProductIds.has(product.id)) return false;
      seenProductIds.add(product.id);
      return true;
    });
};

export const normalizeFeatureSkuForStage3 = (sku, productType, hierarchySkus = [], preMatchedRecord) => {
  if (!sku || typeof sku !== "object") return null;

  // preMatchedRecord === undefined → legacy call, do our own lookup.
  // preMatchedRecord === null      → caller already determined there is no match.
  const hierarchyMatch =
    preMatchedRecord !== undefined ? preMatchedRecord : findHierarchySkuMatch(sku, hierarchySkus);
  const levelIds = extractFeatureLevelIds(sku);
  const skuId = getFeatureSkuId(sku);
  const skuCode = getFeatureSkuCode(sku);
  const skuName = getFeatureSkuName(sku);

  return {
    id: hierarchyMatch?.id || buildFeatureProductId(levelIds, skuId),
    breadcrumb: hierarchyMatch?.breadcrumb || buildFeatureBreadcrumb(productType, sku, skuName, skuCode),
    sku_id: hierarchyMatch?.sku_id || hierarchyMatch?.skuId || hierarchyMatch?.id?.split("_").pop() || skuId,
    sku_code: skuCode || hierarchyMatch?.sku_code || sku?.sku_code || sku?.skuCode,
    sku_description: skuName || hierarchyMatch?.name || sku?._description,
    level1_id: hierarchyMatch?.level1_id ?? levelIds[0] ?? 0,
    level2_id: hierarchyMatch?.level2_id ?? levelIds[1] ?? 0,
    level3_id: hierarchyMatch?.level3_id ?? levelIds[2] ?? 0,
    level4_id: hierarchyMatch?.level4_id ?? levelIds[3] ?? 0,
    level5_id: hierarchyMatch?.level5_id ?? levelIds[4] ?? 0,
    level6_id: hierarchyMatch?.level6_id ?? hierarchyMatch?.sku_id ?? hierarchyMatch?.id?.split("_").pop() ?? levelIds[5] ?? skuId,
    featureSku: sku,
  };
};

export const pruneSelectedFilters = (selectedFilters, availableFilters) =>
  Object.entries(selectedFilters || {}).reduce((acc, [key, values]) => {
    const availableValues = new Set((availableFilters?.[key] || []).map((option) => option.value));
    const currentValues = Array.isArray(values) ? values : values ? [values] : [];
    const nextValues = currentValues.filter((value) => availableValues.has(value));

    if (nextValues.length) {
      acc[key] = nextValues;
    }

    return acc;
  }, {});

const normalizeFilterValues = (values) => {
  const safeValues = Array.isArray(values) ? values : values ? [values] : [];

  return safeValues
    .map((item) => {
      if (item === null || item === undefined) return null;
      const value =
        typeof item === "object"
          ? item.value ?? item.name ?? item.label ?? item.filter_value ?? item.feature_value
          : item;
      return value === null || value === undefined
        ? null
        : { label: String(value), value: String(value) };
    })
    .filter(Boolean);
};

const normalizeSku = (sku) => {
  if (!sku || typeof sku !== "object") return null;

  const skuCode =
    sku.sku_code ||
    sku.skuCode ||
    sku.sku_id ||
    sku.skuId ||
    sku.code ||
    sku.id ||
    sku.SKU_CODE;

  const description =
    sku.sku_description ||
    sku.skuDescription ||
    sku.description ||
    sku.sku_name ||
    sku.skuName ||
    sku.name ||
    sku.Description;

  return {
    ...sku,
    _rowId: skuCode ? String(skuCode) : `fallback_${Math.random().toString(36).substr(2, 9)}`,
    _skuCode: skuCode ? String(skuCode) : "-",
    _description: description ? String(description) : "-",
    _featureSummary: buildFeatureSummary(sku),
  };
};

const buildFeatureSummary = (sku) => {
  const explicitSummary =
    sku.feature_summary ||
    sku.featureSummary ||
    sku.features_summary ||
    sku.Features ||
    sku.features;

  if (typeof explicitSummary === "string") return explicitSummary;
  if (Array.isArray(explicitSummary)) return explicitSummary.filter(Boolean).join(", ");
  if (explicitSummary && typeof explicitSummary === "object") {
    return Object.entries(explicitSummary)
      .filter(([, value]) => value !== null && value !== undefined && value !== "")
      .map(([key, value]) => `${key}: ${formatValue(value)}`)
      .join(", ");
  }

  return Object.entries(sku)
    .filter(([key, value]) => !isSkuIdentityKey(key) && value !== null && value !== undefined && value !== "")
    .slice(0, 6)
    .map(([key, value]) => `${key}: ${formatValue(value)}`)
    .join(", ");
};

const findHierarchySkuMatch = (sku, hierarchySkus) => {
  if (!Array.isArray(hierarchySkus) || !hierarchySkus.length) return null;

  const skuId = getFeatureSkuId(sku) ? String(getFeatureSkuId(sku)).trim().toLowerCase() : "";
  const skuCode = getFeatureSkuCode(sku) ? String(getFeatureSkuCode(sku)).trim().toLowerCase() : "";
  const skuName = getFeatureSkuName(sku) ? String(getFeatureSkuName(sku)).trim().toLowerCase() : "";

  // 1. Exact match by sku_id or last part of the hierarchy ID
  let match = hierarchySkus.find((record) => {
    const recordSkuId = String(record.sku_id ?? record.skuId ?? record.id ?? "").toLowerCase();
    const lastIdPart = String(record.id || "").split("_").pop().toLowerCase();
    return skuId && (recordSkuId === skuId || lastIdPart === skuId);
  });
  if (match) return match;

  // 2. Exact match by sku_code
  match = hierarchySkus.find((record) => {
    const recordCode = String(record.sku_code ?? record.skuCode ?? record.code ?? record.SKU_CODE ?? "").toLowerCase();
    return skuCode && recordCode && recordCode === skuCode;
  });
  if (match) return match;
  
  // 3. Substring match: record name contains sku_code
  match = hierarchySkus.find((record) => {
    const recordName = String(record.name ?? record.sku_name ?? record.skuName ?? record.breadcrumb?.split("\\").pop() ?? "").toLowerCase();
    return skuCode && recordName && recordName.includes(skuCode);
  });
  if (match) return match;

  // 4. Exact match by sku_name
  match = hierarchySkus.find((record) => {
    const recordName = String(record.name ?? record.sku_name ?? record.skuName ?? record.breadcrumb?.split("\\").pop() ?? "").toLowerCase();
    return skuName && recordName && recordName === skuName;
  });
  if (match) return match;

  // 5. Substring match: record name contains sku_name or vice versa
  match = hierarchySkus.find((record) => {
    const recordName = String(record.name ?? record.sku_name ?? record.skuName ?? record.breadcrumb?.split("\\").pop() ?? "").toLowerCase();
    return skuName && recordName && (recordName.includes(skuName) || skuName.includes(recordName));
  });

  return match || null;
};

const extractFeatureLevelIds = (sku) => [1, 2, 3, 4, 5, 6].map((level) =>
  sku?.[`level${level}_id`] ?? sku?.[`level${level}Id`] ?? sku?.[`LEVEL${level}_ID`] ?? null
);

const buildFeatureProductId = (levelIds, skuId) => {
  const normalizedLevels = levelIds.map((level) =>
    level === null || level === undefined || level === "" ? 0 : level
  );

  if (normalizedLevels.some((level) => level !== 0)) {
    return normalizedLevels.join("_");
  }

  return `0_0_0_0_0_${skuId || `unknown_${Math.random().toString(36).substr(2, 9)}`}`;
};

const buildFeatureBreadcrumb = (productType, sku, skuName, skuCode) => {
  const fallbackName = skuName || skuCode || "Unknown SKU";
  const explicitBreadcrumb = sku?.breadcrumb || sku?.product_breadcrumb || sku?.productBreadcrumb;

  if (explicitBreadcrumb) return String(explicitBreadcrumb);
  if (productType) return `${productType}\\${fallbackName}`;
  return fallbackName;
};

const formatValue = (value) => (Array.isArray(value) ? value.join(", ") : String(value));

const isSkuIdentityKey = (key) => {
  const normalized = key.toLowerCase();
  return [
    "sku_code",
    "skucode",
    "sku_id",
    "skuid",
    "id",
    "code",
    "sku_description",
    "skudescription",
    "description",
    "sku_name",
    "skuname",
    "name",
  ].includes(normalized);
};

const getFirstNumber = (source, keys) => {
  if (!source || typeof source !== "object") return undefined;

  for (const key of keys) {
    const value = Number(source[key]);
    if (Number.isFinite(value)) return value;
  }

  return undefined;
};
